/**
 * Hook for managing PRD session status and operations.
 *
 * Provides real-time status polling, cancellation, and cross-channel visibility
 * for PRD generation sessions initiated via web UI, MCP, or CLI.
 */

import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelSession,
  getSessionStatusBatch,
  type ProjectSessionStatusBatchItem,
} from '~/services/api/prd';

export interface UsePRDSessionStatusBatchResult {
  batchData:
    | {
        projects: ProjectSessionStatusBatchItem[];
      }
    | undefined;
  isLoading: boolean;
}

export interface UsePRDSessionStatusResult {
  status: ProjectSessionStatusBatchItem;
  isLoading: boolean;
  cancel: () => Promise<void>;
  isCanceling: boolean;
}

/**
 * Hook for reading PRD session status for a single project.
 *
 * This hook uses React Query's automatic deduplication - all components using
 * this hook share the same batch query (only one network request).
 *
 * Features:
 * - Shared batch query (deduped by React Query)
 * - Automatic updates when any component invalidates
 * - Cancellation with query invalidation
 *
 * @param projectId - Project UUID to track session for
 * @returns Session status, loading state, and cancel function
 */
export function usePRDSessionStatus(projectId: string): UsePRDSessionStatusResult {
  const queryClient = useQueryClient();

  // React Query deduplicates queries with the same key
  // All ProjectCards share ONE batch query, each component subscribes and re-renders on updates
  const { data: batchData, isLoading } = useQuery({
    queryKey: ['prd-session-status-batch'],
    queryFn: getSessionStatusBatch,
    staleTime: 10000,
    refetchInterval: query => {
      const hasActiveSession = query.state.data?.projects.some(
        p => p.status === 'active' || p.status === 'paused',
      );
      return hasActiveSession ? 10000 : false;
    },
  });

  // Find this project's status in the batch data, or return empty state
  const status =
    batchData?.projects.find(p => p.project_id === projectId) ||
    ({
      project_id: projectId,
      session_id: null,
      status: 'completed',
      session_state: null,
      generation_phase: null,
      completeness_score: 0,
      question_count: 0,
      answered_question_count: 0,
      created_at: null,
      updated_at: null,
      is_owner: false,
    } as ProjectSessionStatusBatchItem);

  // Use a ref to avoid stale closure in mutation function
  // Without this, mutationFn would close over the old status value
  const statusRef = useRef(status);
  statusRef.current = status;

  // Cancel uses session_id from status ref to get latest value
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const currentStatus = statusRef.current;
      if (!currentStatus?.session_id) throw new Error('No active session to cancel');
      await cancelSession(currentStatus.session_id);
    },
    onSuccess: () => {
      // Invalidate batch query to refresh all project statuses
      void queryClient.invalidateQueries({ queryKey: ['prd-session-status-batch'] });
      // Invalidate projects list to update any project-level indicators
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  return {
    status,
    isLoading,
    cancel: cancelMutation.mutateAsync,
    isCanceling: cancelMutation.isPending,
  };
}

/**
 * Hook for fetching all project session statuses in a single batch query.
 *
 * This is the primary hook that should be called once at the project list level.
 * Individual ProjectCards then use `usePRDSessionStatus(projectId)` which reads from this cache.
 *
 * Features:
 * - Single batch query instead of N individual queries
 * - Automatic polling every 10 seconds when any session is active/paused
 * - Populates individual project caches for instant reads
 * - Polling stops automatically when no sessions are active (efficient)
 *
 * @returns Batch status data and loading state
 *
 * @example
 * // In ProjectList component:
 * const { batchData, isLoading } = usePRDSessionStatusBatch();
 *
 * // In ProjectCard component:
 * const { status } = usePRDSessionStatus(projectId); // Reads from batch cache
 */
export function usePRDSessionStatusBatch(): UsePRDSessionStatusBatchResult {
  const queryClient = useQueryClient();

  const { data: batchData, isLoading } = useQuery({
    queryKey: ['prd-session-status-batch'],
    queryFn: async () => {
      const batch = await getSessionStatusBatch();

      // Populate individual project caches for instant reads
      batch.projects.forEach(projectStatus => {
        queryClient.setQueryData(['prd-session-status', projectStatus.project_id], projectStatus);
      });

      return batch;
    },
    refetchInterval: query => {
      // Poll every 10 seconds if ANY project has an active or paused session
      const hasActiveSession = query.state.data?.projects.some(
        p => p.status === 'active' || p.status === 'paused',
      );

      // Stop polling when no sessions are active/paused (efficient)
      return hasActiveSession ? 10000 : false;
    },
    staleTime: 10000, // Keep data fresh for 10 seconds
  });

  return {
    batchData,
    isLoading,
  };
}
